// HudTooltip — singleton portal for inline term tooltips on the HUD.
//
// Why a portal instead of CSS ::after? The HUD panels (.hud__resources,
// .hud-context__panel) use clip-path polygons for their chamfered
// borders. clip-path clips descendants too, so an absolutely-positioned
// ::after tooltip child gets cropped at the panel edge — exactly where
// the tooltip needs to escape. A single tooltip element parented to
// document.body and positioned via getBoundingClientRect dodges every
// clip-path on its way out.
//
// One tooltip DOM node, lazy-created on first hover. Event delegation
// reads [data-tooltip] anywhere under #hud. Touch profile suppresses
// the listener — tap-and-hold isn't a discoverable interaction and the
// pre-match IntroOverlay already covers the same terminology for
// mobile players.
//
// Positioning: above the source by default; flips below if it would
// overflow the top of the viewport, and clamps horizontally so it
// never spills off the side. No arrow — keeps the implementation tight
// and the visual restraint matches the rest of the V1.6 hairline
// outline / chip-style HUD language.

import { isTouchProfile } from '../input/Profile.js';
import { injectHudThemeStyles } from './hudThemeStyles.js';

const TOOLTIP_ID = 'hud-tooltip-portal';
const STYLE_ID = 'hud-tooltip-portal-styles';
const SHOW_DELAY_MS = 220;
const MAX_WIDTH_PX = 260;
const VIEWPORT_MARGIN_PX = 8;

let installed = false;
let tooltipEl: HTMLDivElement | null = null;
let currentTarget: Element | null = null;
let showTimer: ReturnType<typeof setTimeout> | null = null;

export function installHudTooltips(): void {
  if (installed) return;
  if (typeof document === 'undefined') return;
  // Skip the listener entirely on touch — hover isn't reliable there and
  // the IntroOverlay covers the same terminology up front. Cheap to call
  // here because Profile.ts caches the answer.
  if (isTouchProfile()) return;
  installed = true;
  injectStyles();

  // Event delegation on document. Using mouseover/mouseout (not enter/leave)
  // because they bubble — one set of handlers catches every [data-tooltip]
  // anywhere under #hud, including elements re-rendered each frame.
  document.addEventListener('mouseover', onMouseOver, { passive: true });
  document.addEventListener('mouseout', onMouseOut, { passive: true });
  // Hide on scroll/resize so a stale bubble doesn't float in a wrong spot.
  window.addEventListener('scroll', hide, { passive: true });
  window.addEventListener('resize', hide, { passive: true });
}

function onMouseOver(ev: MouseEvent): void {
  const target = (ev.target as Element | null)?.closest('[data-tooltip]') ?? null;
  if (target === null) return;
  // Only honor tooltips inside the HUD subtree. Other [data-tooltip] users
  // (none today, but possible elsewhere) opt in by scoping their own.
  if (target.closest('#hud') === null) return;
  if (target === currentTarget) return;
  currentTarget = target;
  if (showTimer !== null) clearTimeout(showTimer);
  showTimer = setTimeout(() => show(target), SHOW_DELAY_MS);
}

function onMouseOut(ev: MouseEvent): void {
  const target = (ev.target as Element | null)?.closest('[data-tooltip]') ?? null;
  if (target === null) return;
  // Only hide when leaving the actual tooltip-bearing element entirely.
  // mouseout fires when crossing into descendants, so check relatedTarget.
  const related = ev.relatedTarget as Element | null;
  if (related !== null && target.contains(related)) return;
  if (target === currentTarget) hide();
}

function show(target: Element): void {
  const text = target.getAttribute('data-tooltip');
  if (text === null || text.length === 0) return;
  const el = ensureTooltipEl();
  el.textContent = text;
  el.style.display = 'block';
  position(el, target);
}

function hide(): void {
  if (showTimer !== null) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  currentTarget = null;
  if (tooltipEl !== null) tooltipEl.style.display = 'none';
}

function position(el: HTMLDivElement, target: Element): void {
  const rect = target.getBoundingClientRect();
  // Measure the tooltip after content set so width is realistic.
  // Reset positioning to compute natural size first.
  el.style.left = '0px';
  el.style.top = '0px';
  const tipRect = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Default: above the target, horizontally centered.
  let top = rect.top - tipRect.height - 8;
  let left = rect.left + rect.width / 2 - tipRect.width / 2;

  // Flip below if the bubble would clip the top of the viewport.
  if (top < VIEWPORT_MARGIN_PX) {
    top = rect.bottom + 8;
  }
  // Clamp horizontally so it never spills off the side.
  if (left < VIEWPORT_MARGIN_PX) left = VIEWPORT_MARGIN_PX;
  if (left + tipRect.width > vw - VIEWPORT_MARGIN_PX) {
    left = vw - VIEWPORT_MARGIN_PX - tipRect.width;
  }
  // Clamp vertically too — if both above and below overflow (very short
  // viewport), prefer pinning to the bottom of the source so the tooltip
  // is at least visible.
  if (top + tipRect.height > vh - VIEWPORT_MARGIN_PX) {
    top = Math.max(VIEWPORT_MARGIN_PX, vh - VIEWPORT_MARGIN_PX - tipRect.height);
  }

  el.style.left = `${Math.round(left)}px`;
  el.style.top = `${Math.round(top)}px`;
}

function ensureTooltipEl(): HTMLDivElement {
  if (tooltipEl !== null) return tooltipEl;
  const div = document.createElement('div');
  div.id = TOOLTIP_ID;
  div.style.display = 'none';
  document.body.appendChild(div);
  tooltipEl = div;
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${TOOLTIP_ID} {
  position: fixed;
  z-index: 90;
  max-width: ${MAX_WIDTH_PX}px;
  padding: 7px 10px;
  background: rgba(8, 12, 18, 0.97);
  border: 1px solid #4a96ff;
  color: #d4dae2;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  line-height: 1.45;
  letter-spacing: 0.02em;
  text-align: left;
  white-space: normal;
  pointer-events: none;
  box-shadow: 0 6px 20px rgba(0,0,0,0.6), 0 0 0 1px rgba(74,150,255,0.18);
  animation: hud-tooltip-fade 140ms ease-out;
}
@keyframes hud-tooltip-fade {
  0%   { opacity: 0; }
  100% { opacity: 1; }
}
  `;
  document.head.appendChild(style);
  injectHudThemeStyles();
}
